/**
 * Kumbh Sukrit Accommodation Configuration
 * Single source of truth for all accommodation types, labels, images, amenities, and i18n keys
 */
import { TentTypeV2 } from '@camp67/model';
import { images } from './images';
import { PageRoutes } from '../pages/page-routes';
import { TranslationKey } from '../pages/pages-types';

/**
 * Accommodation type - uses TentTypeV2 enum values as strings
 */
export type AccommodationType =
  | 'mudhouse'
  | 'mudhouse-dormitory'
  | 'premium-twin-bed-cottage';

/**
 * Maps AccommodationType strings to TentTypeV2 enum
 */
export const ACCOMMODATION_TO_TENT_TYPE: Record<AccommodationType, TentTypeV2> = {
  mudhouse: TentTypeV2.MUDHOUSE,
  'mudhouse-dormitory': TentTypeV2.MUDHOUSE_DORMITORY,
  'premium-twin-bed-cottage': TentTypeV2.PREMIUM_TWIN_COTTAGE,
};

/**
 * Gallery image with optional portrait flag
 * Portrait images won't use object-fit: cover to avoid cropping
 */
export interface GalleryImage {
  imageUrl: string;
  /** If true, image is portrait orientation and should use object-fit: contain */
  portrait?: boolean;
}

/**
 * Amenity configuration with i18n keys
 */
interface AmenityConfig {
  summary: string; // i18n key
  detail: string; // i18n key
}

/**
 * Full accommodation configuration
 */
export interface AccommodationConfig {
  /** Accommodation slug (matches nivaas room offering) */
  slug: AccommodationType;
  /** TentTypeV2 enum value for booking system */
  tentType: TentTypeV2;
  /** Display label - hardcoded (used in emails, non-i18n contexts) */
  label: string;
  /** i18n key for title */
  titleKey: TranslationKey;
  /** i18n key for short title (used in tight spaces) */
  shortTitleKey: TranslationKey;
  /** i18n key for description */
  descriptionKey: TranslationKey;
  /** i18n key for image alt text */
  imageAltKey: string;
  /** Description for forms (hardcoded) */
  formDescription: string;
  /** Capacity (guests per room) */
  capacity: number;
  /** Hero/main image URL */
  heroImage: string;
  /** Gallery images */
  gallery: GalleryImage[];
  /** Whether room has private bathroom */
  hasPrivateBathroom: boolean;
  /** Whether this is a premium offering */
  isPremium: boolean;
  /** Display order (lower = shown first) */
  displayOrder: number;
  /** Path to detail page */
  detailPagePath: PageRoutes;
  /** Tent-specific amenities */
  tentAmenities: AmenityConfig[];
  /** Camp-wide amenities */
  campAmenities: AmenityConfig[];
}

/**
 * Shared camp amenities (same for all accommodations)
 */
const CAMP_AMENITIES: AmenityConfig[] = [
  {
    summary: 'ks-accommodation.amenity.sattvic-meals.title',
    detail: 'ks-accommodation.amenity.sattvic-meals.description',
  },
  {
    summary: 'ks-accommodation.amenity.guided-experiences.title',
    detail: 'ks-accommodation.amenity.guided-experiences.description',
  },
  {
    summary: 'ks-accommodation.amenity.security.title',
    detail: 'ks-accommodation.amenity.security.description',
  },
];

/**
 * Master accommodation configuration
 * All accommodation data is defined here
 */
export const ACCOMMODATIONS: Record<AccommodationType, AccommodationConfig> = {
  mudhouse: {
    slug: 'mudhouse',
    tentType: TentTypeV2.MUDHOUSE,
    label: 'Sanskriti Vann Kutiram (Mudhouses)',
    titleKey: 'home:accommodation.mudhouse.title',
    shortTitleKey: 'home:accommodation.mudhouse.short-title',
    descriptionKey: 'home:accommodation.mudhouse.description',
    imageAltKey: 'ks-accommodation.mudhouse.imageAlt',
    formDescription: 'Premium mudhouse with luxurious interiors',
    capacity: 2,
    heroImage: images['mudhouse-hero'],
    gallery: [
      { imageUrl: images['mudhouse-2025-internal-3'] },
      { imageUrl: images['mudhouse-2025-internal-1'] },
      { imageUrl: images['mudhouse-2025-internal-2'] },
      { imageUrl: images['mudhouse-2025-internal-4'] },
      { imageUrl: images['mudhouse-2025-internal-5'] },
      { imageUrl: images['mudhouse-2025-internal-6'] },
      { imageUrl: images['mudhouse-2025-external-1'] },
      { imageUrl: images['mudhouse-2025-external-2'] },
      { imageUrl: images['mudhouse-2025-external-3'] },
      { imageUrl: images['mudhouse-2025-external-4'] },
      { imageUrl: images['mudhouse-2025-external-5'] },
      { imageUrl: images['mudhouse-2025-external-6'] },
      { imageUrl: images['mudhouse-2025-external-7'] },
      { imageUrl: images['mudhouse-2025-external-8'] },
      { imageUrl: images['mudhouse-detail'] },
      { imageUrl: images['mudhouse-8'], portrait: true },
      { imageUrl: images['mudhouse-9'], portrait: true },
    ],
    hasPrivateBathroom: true,
    isPremium: false,
    displayOrder: 1,
    detailPagePath: PageRoutes.mudhouse,
    tentAmenities: [
      {
        summary: 'ks-accommodation.amenity.traditional-design.title',
        detail: 'ks-accommodation.amenity.traditional-design.description',
      },
      {
        summary: 'ks-accommodation.amenity.private-bathroom.title',
        detail: 'ks-accommodation.amenity.private-bathroom.description',
      },
      {
        summary: 'ks-accommodation.amenity.comfortable-bedding.title',
        detail: 'ks-accommodation.amenity.comfortable-bedding.description',
      },
    ],
    campAmenities: CAMP_AMENITIES,
  },
  'mudhouse-dormitory': {
    slug: 'mudhouse-dormitory',
    tentType: TentTypeV2.MUDHOUSE_DORMITORY,
    label: 'Sanskriti Vann Sangham (Dormitory)',
    titleKey: 'home:accommodation.dormitory.title',
    shortTitleKey: 'home:accommodation.dormitory.short-title',
    descriptionKey: 'home:accommodation.dormitory.description',
    imageAltKey: 'ks-accommodation.mudhouse-dormitory.imageAlt',
    formDescription: 'Affordable shared accommodation in traditional mudhouses',
    capacity: 1,
    heroImage: images['mudhouse-dorm-external-1'],
    gallery: [
      { imageUrl: images['mudhouse-dorm-internal-1'] },
      { imageUrl: images['mudhouse-dorm-internal-2'] },
      { imageUrl: images['mudhouse-dorm-internal-3'], portrait: true },
      { imageUrl: images['mudhouse-dorm-internal-4'], portrait: true },
      { imageUrl: images['mudhouse-dorm-internal-5'], portrait: true },
      { imageUrl: images['mudhouse-dorm-external-1'] },
      { imageUrl: images['mudhouse-dorm-external-3'] },
    ],
    hasPrivateBathroom: false,
    isPremium: false,
    displayOrder: 2,
    detailPagePath: PageRoutes.mudhouseDormitory,
    tentAmenities: [
      {
        summary: 'ks-accommodation.amenity.traditional-design.title',
        detail: 'ks-accommodation.amenity.traditional-design.description',
      },
      {
        summary: 'ks-accommodation.amenity.shared-bathroom.title',
        detail: 'ks-accommodation.amenity.shared-bathroom.description',
      },
      {
        summary: 'ks-accommodation.amenity.comfortable-bedding.title',
        detail: 'ks-accommodation.amenity.comfortable-bedding.description',
      },
    ],
    campAmenities: CAMP_AMENITIES,
  },
  'premium-twin-bed-cottage': {
    slug: 'premium-twin-bed-cottage',
    tentType: TentTypeV2.PREMIUM_TWIN_COTTAGE,
    label: 'Sanskriti Vann Mandapam (Premium Tents)',
    titleKey: 'home:accommodation.premium-tent.title',
    shortTitleKey: 'home:accommodation.premium-tent.short-title',
    descriptionKey: 'home:accommodation.premium-tent.description',
    imageAltKey: 'ks-accommodation.premium-twin-cottage.imageAlt',
    formDescription: 'Spacious room with two single beds and elegant furnishings',
    capacity: 2,
    heroImage: images['premium-twin-cottage-2025-hero'],
    gallery: [
      { imageUrl: images['premium-twin-cottage-2025-internal-2'] },
      { imageUrl: images['premium-twin-cottage-2025-internal-1'] },
      { imageUrl: images['premium-twin-cottage-1'] },
      { imageUrl: images['premium-twin-cottage-4'], portrait: true },
      { imageUrl: images['premium-twin-cottage-2025-internal-3'], portrait: true },
      { imageUrl: images['premium-twin-cottage-2025-internal-4'], portrait: true },
      { imageUrl: images['premium-twin-cottage-2025-external-1'] },
      { imageUrl: images['premium-twin-cottage-2025-external-2'] },
      { imageUrl: images['premium-twin-cottage-2025-external-3'] },
      { imageUrl: images['premium-twin-cottage-2025-external-5'] },
      { imageUrl: images['premium-twin-cottage-2025-external-6'] },
    ],
    hasPrivateBathroom: true,
    isPremium: false,
    displayOrder: 3,
    detailPagePath: PageRoutes.premiumTwinTent,
    tentAmenities: [
      {
        summary: 'ks-accommodation.amenity.premium-furnishings.title',
        detail: 'ks-accommodation.amenity.premium-furnishings.description',
      },
      {
        summary: 'ks-accommodation.amenity.private-bathroom.title',
        detail: 'ks-accommodation.amenity.private-bathroom.description',
      },
      {
        summary: 'ks-accommodation.amenity.twin-beds.title',
        detail: 'ks-accommodation.amenity.twin-beds.description',
      },
    ],
    campAmenities: CAMP_AMENITIES,
  },
};

/**
 * Display labels for each accommodation type
 * Used in emails, non-i18n contexts, etc.
 */
export const ACCOMMODATION_LABELS: Record<AccommodationType, string> = {
  mudhouse: ACCOMMODATIONS.mudhouse.label,
  'mudhouse-dormitory': ACCOMMODATIONS['mudhouse-dormitory'].label,
  'premium-twin-bed-cottage': ACCOMMODATIONS['premium-twin-bed-cottage'].label,
};

/**
 * Options for forms/dropdowns - backward compatible format
 */
export const accommodationOptions = Object.values(ACCOMMODATIONS).map((config) => ({
  value: config.slug,
  label: config.label,
  description: config.formDescription,
  capacity: config.capacity,
}));

/**
 * Get accommodations sorted by display order
 */
export function getAccommodationsSorted(): AccommodationConfig[] {
  return Object.values(ACCOMMODATIONS).sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Get accommodation by slug
 */
export function getAccommodation(slug: AccommodationType): AccommodationConfig {
  return ACCOMMODATIONS[slug];
}
